/**
 * UI 组件层：把原型 CSS 里的类名包成语义化组件。
 *
 * 为什么不引入组件库：原型这套深色金融风视觉是定制的，成品库会与其打架；
 * 而 theme.css 里已经有卡片/徽章/按钮/时间轴/开关/弹层等完整样式定义，
 * 包装成本很低。
 */
import type { CSSProperties, ReactNode } from 'react';

/* ---------------- 卡片 ---------------- */

interface CardProps {
  title?: ReactNode;
  more?: ReactNode;
  children: ReactNode;
  className?: string;
  tight?: boolean;
  style?: CSSProperties;
}

export function Card({ title, more, children, className = '', tight, style }: CardProps) {
  return (
    <div className={`card${tight ? ' tight' : ''} ${className}`.trim()} style={style}>
      {title !== undefined && (
        <div className="card-hd">
          <div className="card-tt bar">{title}</div>
          {more !== undefined && <div className="card-more">{more}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

export function SectionTitle({ children, sub }: { children: ReactNode; sub?: ReactNode }) {
  return (
    <div className="sec-title">
      {children}
      {sub !== undefined && <small>{sub}</small>}
    </div>
  );
}

/* ---------------- 列表项 ---------------- */

interface ListItemProps {
  icon?: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  right?: ReactNode;
  onClick?: () => void;
}

export function ListItem({ icon, title, sub, right, onClick }: ListItemProps) {
  const content = (
    <>
      {icon !== undefined && <div className="li-ico">{icon}</div>}
      <div className="li-main">
        <div className="li-t">{title}</div>
        {sub !== undefined && <div className="li-s">{sub}</div>}
      </div>
      {right !== undefined && <div className="li-r">{right}</div>}
    </>
  );
  if (onClick) {
    return (
      <button type="button" className="list-item as-button" onClick={onClick} style={{ width: '100%', textAlign: 'left' }}>
        {content}
      </button>
    );
  }
  return <div className="list-item">{content}</div>;
}

/* ---------------- 徽章 / 按钮 ---------------- */

type BadgeTone = 'h' | 'e' | 'w' | 'ok' | 'warn' | 'danger' | 'ghost';

export function Badge({ tone = 'ghost', children }: { tone?: BadgeTone; children: ReactNode }) {
  return <span className={`badge ${tone}`}>{children}</span>;
}

interface ButtonProps {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'primary' | 'gold' | 'danger';
  block?: boolean;
  small?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit';
}

export function Button({ children, onClick, variant = 'default', block, small, disabled, type = 'button' }: ButtonProps) {
  const cls = ['btn', variant !== 'default' ? variant : '', block ? 'block' : '', small ? 'sm' : ''].filter(Boolean).join(' ');
  return (
    <button type={type} className={cls} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

/* ---------------- 指标块 ---------------- */

export function Kpi({ label, value, delta, tone }: { label: ReactNode; value: ReactNode; delta?: ReactNode; tone?: 'h' | 'e' | 'w' | 'danger' }) {
  return (
    <div className="kpi">
      <div className="k-l">{label}</div>
      <div className={`k-v${tone && tone !== 'h' ? ` ${tone}` : ''}`}>{value}</div>
      {delta !== undefined && <div className="k-d">{delta}</div>}
    </div>
  );
}

export function ProgressBar({ value, color = 'var(--h)', max = 100 }: { value: number; color?: string; max?: number }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className="bar-track">
      <div className="bar-fill" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

/* ---------------- 预警条 ---------------- */

export function AlertStrip({
  tone,
  title,
  sub,
  onClick,
}: {
  tone: 'warn' | 'danger' | 'ok';
  title: ReactNode;
  sub?: ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      className={`alert-strip ${tone}`}
      onClick={onClick}
      style={{ width: '100%', textAlign: 'left', border: 'none' }}
    >
      <span className="as-ico">{tone === 'ok' ? '✓' : tone === 'danger' ? '!' : '⚠'}</span>
      <span className="grow">
        <span className="as-t">{title}</span>
        {sub !== undefined && <span className="as-s">{sub}</span>}
      </span>
    </button>
  );
}

/* ---------------- 任务 ---------------- */

export function TaskItem({
  title,
  sub,
  done,
  onToggle,
  busy,
}: {
  title: ReactNode;
  sub?: ReactNode;
  done: boolean;
  onToggle: () => void;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      className={`task${done ? ' done' : ''}`}
      onClick={onToggle}
      disabled={busy}
      style={{ width: '100%', textAlign: 'left', border: 'none', opacity: busy ? 0.6 : 1 }}
    >
      <span className="tick">
        <svg viewBox="0 0 24 24">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      </span>
      <span className="grow">
        <span className="tk-t">{title}</span>
        {sub !== undefined && <span className="tk-s">{sub}</span>}
      </span>
    </button>
  );
}

/* ---------------- 开关 ---------------- */

export function Switch({
  on,
  onToggle,
  busy,
  label,
}: {
  on: boolean;
  onToggle: () => void;
  busy?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      className={`switch${on ? ' on' : ''}`}
      role="switch"
      aria-checked={on}
      aria-label={label}
      aria-busy={busy === true}
      onClick={onToggle}
    />
  );
}

/* ---------------- 头像 ---------------- */

export function Avatar({ char, gold }: { char: string; gold?: boolean }) {
  return <div className={`avatar${gold ? ' gold' : ''}`}>{char}</div>;
}

/* ---------------- 时间轴 ---------------- */

export function Timeline({
  items,
}: {
  items: { id: string; date: ReactNode; title: ReactNode; desc?: ReactNode; isCurrent?: boolean }[];
}) {
  return (
    <div className="tl">
      {items.map((it) => (
        <div key={it.id} className={`tl-item${it.isCurrent ? ' now' : ''}`}>
          <div className="tl-d">{it.date}</div>
          <div className="tl-t">{it.title}</div>
          {it.desc !== undefined && <div className="tl-s">{it.desc}</div>}
        </div>
      ))}
    </div>
  );
}

/* ---------------- 状态占位 ---------------- */

export function StateBox({
  kind = 'empty',
  title,
  desc,
  action,
}: {
  kind?: 'empty' | 'error' | 'loading';
  title: string;
  desc?: string;
  action?: ReactNode;
}) {
  return (
    <div className={`state-box ${kind === 'error' ? 'error' : ''}`}>
      {kind === 'loading' ? <div className="spinner" /> : <div className="st-ico">{kind === 'error' ? '⚠' : '◍'}</div>}
      <div className="st-title">{title}</div>
      {desc !== undefined && <div>{desc}</div>}
      {action}
    </div>
  );
}

export function SkeletonList({ rows = 3 }: { rows?: number }) {
  return (
    <div className="page-pad">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton skel-card" />
      ))}
    </div>
  );
}

/* ---------------- 表格 ---------------- */

export function Table({ head, rows }: { head: ReactNode[]; rows: ReactNode[][] }) {
  return (
    <table className="tbl">
      <thead>
        <tr>
          {head.map((h, i) => (
            <th key={i}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((c, j) => (
              <td key={j}>{c}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ---------------- 图表宿主 ---------------- */

/**
 * 图表引擎返回 SVG/HTML 字符串，这里统一渲染。
 * 安全性说明：字符串来自本地图表引擎，文本经其内部 esc() 转义；
 * 传入的数值均为后端返回的数字，因此不存在注入面。
 */
export function Chart({ html, className }: { html: string; className?: string }) {
  return <div className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}

/* ---------------- 底部弹层 ---------------- */

export function Sheet({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="overlay on" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-grip" />
        <div className="sheet-hd">
          <div className="sheet-tt">{title}</div>
          <button type="button" className="close-x" onClick={onClose} aria-label="关闭">
            ✕
          </button>
        </div>
        <div className="sheet-body">{children}</div>
      </div>
    </div>
  );
}
